/* ATTAR site layout: drawer menu, search, hero slideshow, journal carousel, films, reveals, back to top.
   Re-initialises after Theme Editor changes. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let lastFocus = null;

  /* ---------- drawer menu and search ---------- */
  function open(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-open'));
    document.documentElement.style.overflow = 'hidden';
    document.querySelectorAll(`[aria-controls="${el.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
    const f = el.querySelector('input, a, button');
    if (f) setTimeout(() => f.focus(), 60);
  }
  function close(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.querySelectorAll(`[aria-controls="${el.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, 450);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-axe-open]');
    if (opener) { e.preventDefault(); open(document.getElementById(opener.getAttribute('aria-controls'))); return; }
    const closer = e.target.closest('[data-axe-close]');
    if (closer) { e.preventDefault(); close(closer.closest('.axe-drawer, .axe-search')); }
    const top = e.target.closest('[data-axe-top]');
    if (top) { e.preventDefault(); scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); }
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') document.querySelectorAll('.axe-drawer.is-open, .axe-search.is-open').forEach(close);
  });

  /* ---------- header shadow, back to top ---------- */
  const onScroll = () => {
    const h = document.querySelector('[data-axe-header]');
    if (h) h.classList.toggle('is-scrolled', scrollY > 8);
    const t = document.querySelector('[data-axe-top]');
    if (t) t.classList.toggle('is-on', scrollY > innerHeight);
  };
  addEventListener('scroll', onScroll, { passive: true });

  /* ---------- slideshow ----------
     Each slide sets how long it stays (data-axe-length):
       auto   = the section's "Seconds per slide"
       video  = until its video ends, whatever its length
       custom = data-axe-seconds
     Videos load only when their slide (or the one before it) is shown, and only the visible slide's video plays. */
  const loadVideo = v => {
    if (v && v.dataset.axeSrc && !v.getAttribute('src')) { v.src = (v.dataset.axeSrcPhone && matchMedia('(max-width: 699px)').matches) ? v.dataset.axeSrcPhone : v.dataset.axeSrc; v.load(); }
  };
  function slideshow(root) {
    if (root.dataset.axeReady) return; root.dataset.axeReady = '1';
    const track = root.querySelector('[data-axe-track]');
    const slides = [...root.querySelectorAll('[data-axe-slide]')];
    const dots = [...root.querySelectorAll('[data-axe-dot]')];
    const videoOf = s => s && s.querySelector('video');
    if (!track || slides.length < 2) {
      slides.forEach(s => { s.classList.add('is-active'); const v = videoOf(s); if (v) { loadVideo(v); if (!reduce) v.play().catch(() => {}); } });
      return;
    }
    const secs = parseFloat(root.dataset.axeAutoplay || '0');
    const afterVideo = root.dataset.axeAfterVideo !== 'false';
    const playBtn = root.querySelector('[data-axe-play]');
    let i = 0, shown = -1, timer = null, paused = false, watch = null, pending = false;

    /* play / replay control: shown when the browser blocks autoplay, and on the last frame when "advance after video" is off */
    const button = state => {
      if (!playBtn) return;
      if (!state) { playBtn.hidden = true; return; }
      playBtn.hidden = false; playBtn.dataset.state = state;
      playBtn.setAttribute('aria-label', state === 'replay' ? 'Replay video' : 'Play video');
    };
    const tryPlay = v => {
      v.muted = true;
      const p = v.play();
      if (p && p.then) p.then(() => button(null)).catch(() => { if (v.paused) button('play'); });
    };
    const clearWait = () => {
      clearTimeout(timer); timer = null;
      if (watch) { watch.off(); watch = null; }
    };
    const go = (n, smooth = true) => {
      i = (n + slides.length) % slides.length;
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    };
    const schedule = () => {
      clearWait();
      const s = slides[i], v = videoOf(s);
      if (v && !v.dataset.axeFailed) {
        /* A video slide never uses the photograph timer. It moves on when the video ends, or when its own custom duration of
           PLAYED time is reached, so nothing advances while the video is loading, buffering or paused. */
        const custom = s.dataset.axeVideo === 'custom' ? parseFloat(s.dataset.axeVideoSeconds || '0') : 0;
        let done = false;
        const finish = () => {
          if (done) return; done = true;
          if (afterVideo && secs > 0 && !reduce) { if (paused) pending = true; else go(i + 1); }
          else { v.pause(); button('replay'); }
        };
        const onTime = () => { if (custom > 0 && v.currentTime >= custom) finish(); };
        const onError = () => { v.dataset.axeFailed = '1'; schedule(); };
        v.addEventListener('ended', finish); v.addEventListener('timeupdate', onTime); v.addEventListener('error', onError);
        watch = { off() { v.removeEventListener('ended', finish); v.removeEventListener('timeupdate', onTime); v.removeEventListener('error', onError); } };
        if (v.ended) finish();
        return;
      }
      if (secs <= 0 || reduce || paused) return;
      const own = parseFloat(s.dataset.axeSeconds || '0');
      const ms = ((s.dataset.axeLength === 'custom' && own > 0) ? own : secs) * 1000;
      timer = setTimeout(() => go(i + 1), ms);
    };
    const show = () => {
      if (shown === i) return;
      shown = i; pending = false; button(null);
      slides.forEach((s, k) => {
        const v = videoOf(s);
        if (!v) return;
        if (k === i) {
          loadVideo(v);
          v.loop = false;                                   /* so that "ended" fires */
          try { v.currentTime = 0; } catch (e) {}           /* a revisited video starts again */
          if (reduce) button('play'); else tryPlay(v);
        } else {
          v.pause();                                        /* only the visible slide's video plays */
        }
      });
      loadVideo(videoOf(slides[(i + 1) % slides.length])); /* get the next slide's video ready */
      schedule();
    };
    const mark = () => {
      const n = Math.round(track.scrollLeft / track.clientWidth);
      i = Math.max(0, Math.min(slides.length - 1, n));
      slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
      dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      show();
    };
    const hold = () => { paused = true; clearTimeout(timer); timer = null; };   /* the video's own listeners stay on */
    const release = () => { paused = false; if (pending) { pending = false; go(i + 1); } else schedule(); };
    track.addEventListener('scroll', () => { clearTimeout(track._t); track._t = setTimeout(mark, 60); }, { passive: true });
    root.querySelectorAll('[data-axe-prev]').forEach(b => b.addEventListener('click', () => go(i - 1)));
    root.querySelectorAll('[data-axe-next]').forEach(b => b.addEventListener('click', () => go(i + 1)));
    dots.forEach((d, k) => d.addEventListener('click', () => go(k)));
    if (playBtn) playBtn.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      const v = videoOf(slides[i]); if (!v) return;
      if (playBtn.dataset.state === 'replay') { try { v.currentTime = 0; } catch (err) {} schedule(); }
      button(null); v.muted = true;
      const p = v.play(); if (p && p.catch) p.catch(() => button('play'));
    });
    root.addEventListener('mouseenter', hold);
    root.addEventListener('mouseleave', release);
    root.addEventListener('focusin', hold);
    root.addEventListener('focusout', e => { if (!root.contains(e.relatedTarget)) release(); });
    /* stop the video while the slideshow is scrolled out of view or the tab is hidden; it resumes where it stopped */
    const resume = () => { const v = videoOf(slides[i]); if (v && !reduce && !v.ended && (!playBtn || playBtn.hidden)) tryPlay(v); };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(es => es.forEach(e => {
        const v = videoOf(slides[i]);
        if (e.isIntersecting) resume();
        else if (v) v.pause();
      }), { threshold: 0.1 }).observe(root);
    }
    document.addEventListener('visibilitychange', () => { const v = videoOf(slides[i]); if (document.hidden) { if (v) v.pause(); } else resume(); });
    mark();
  }

  /* ---------- journal carousel dots ---------- */
  function carousel(root) {
    if (root.dataset.axeReady) return; root.dataset.axeReady = '1';
    const track = root.querySelector('[data-axe-track]');
    const dotsWrap = root.querySelector('[data-axe-dots]');
    if (!track || !dotsWrap) return;
    const build = () => {
      const pages = Math.max(1, Math.ceil(track.scrollWidth / track.clientWidth - 0.05));
      dotsWrap.innerHTML = '';
      if (pages < 2) return;
      for (let k = 0; k < pages; k++) {
        const b = document.createElement('button');
        b.type = 'button'; b.setAttribute('aria-label', 'Page ' + (k + 1));
        b.addEventListener('click', () => track.scrollTo({ left: k * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' }));
        dotsWrap.appendChild(b);
      }
      mark();
    };
    const mark = () => {
      const k = Math.round(track.scrollLeft / track.clientWidth);
      [...dotsWrap.children].forEach((d, n) => d.setAttribute('aria-current', n === k ? 'true' : 'false'));
    };
    track.addEventListener('scroll', () => { clearTimeout(track._t); track._t = setTimeout(mark, 60); }, { passive: true });
    addEventListener('resize', build);
    build();
  }

  /* ---------- films: play silently while visible ---------- */
  const vio = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => {
    const v = e.target;
    if (e.isIntersecting && !v.dataset.userPaused && !reduce) { loadVideo(v); v.play().catch(() => {}); } else if (!e.isIntersecting) v.pause();
  }), { threshold: 0.2 }) : null;
  function film(v) {
    if (v.dataset.axeReady) return; v.dataset.axeReady = '1';
    v.muted = true; v.loop = true; v.playsInline = true;
    const btn = v.parentElement.querySelector('[data-axe-film-toggle]');
    const sync = () => { if (btn) { btn.classList.toggle('is-paused', v.paused); btn.setAttribute('aria-label', v.paused ? 'Play film' : 'Pause film'); } };
    v.addEventListener('play', sync); v.addEventListener('pause', sync);
    if (btn) btn.addEventListener('click', () => { if (v.paused) { delete v.dataset.userPaused; loadVideo(v); v.play(); } else { v.dataset.userPaused = '1'; v.pause(); } });
    if (reduce) v.pause(); else if (vio) vio.observe(v);
    sync();
  }

  /* ---------- reveal ---------- */
  const rio = 'IntersectionObserver' in window && !reduce ? new IntersectionObserver(es => es.forEach(e => {
    if (e.isIntersecting) { e.target.classList.add('is-in'); rio.unobserve(e.target); }
  }), { rootMargin: '0px 0px -10% 0px' }) : null;

  /* ---------- product (centred): photographs ---------- */
  function productGallery(root) {
    if (root.dataset.axeReady) return; root.dataset.axeReady = '1';
    const imgs = [...root.querySelectorAll('[data-axe-img]')];
    root.querySelectorAll('[data-axe-thumb]').forEach(b => b.addEventListener('click', () => {
      imgs.forEach(im => im.classList.toggle('is-active', im.dataset.axeImg === b.dataset.axeThumb));
      root.querySelectorAll('[data-axe-thumb]').forEach(x => x.setAttribute('aria-current', x === b ? 'true' : 'false'));
    }));
  }

  /* variant select keeps the hidden id, price and button in step */
  document.addEventListener('change', e => {
    const sel = e.target.closest('[data-axe-variant]');
    if (!sel) return;
    const form = sel.form, opt = sel.selectedOptions[0];
    form.querySelector('input[name="id"]').value = sel.value;
    const price = document.querySelector('[data-axe-price]');
    if (price && opt.dataset.price) price.innerHTML = opt.dataset.price;
    const add = form.querySelector('[data-axs-add]');
    if (add) { add.disabled = opt.dataset.available !== 'true'; add.querySelector('span').textContent = add.disabled ? 'Sold out' : add.dataset.label; }
    const url = new URL(location.href); url.searchParams.set('variant', sel.value); history.replaceState(null, '', url);
  });

  function init() {
    document.querySelectorAll('[data-axe-slideshow]').forEach(slideshow);
    document.querySelectorAll('[data-axe-carousel]').forEach(carousel);
    document.querySelectorAll('[data-axe-film]').forEach(film);
    document.querySelectorAll('[data-axe-gallery]').forEach(productGallery);
    document.querySelectorAll('[data-axe-reveal]:not(.is-in)').forEach(el => rio ? rio.observe(el) : el.classList.add('is-in'));
    onScroll();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  ['shopify:section:load', 'shopify:section:reorder'].forEach(ev => document.addEventListener(ev, () => setTimeout(init, 50)));
  document.addEventListener('shopify:section:select', e => e.target.querySelectorAll('[data-axe-reveal]').forEach(el => el.classList.add('is-in')));
})();
